import { FormsAnswerReview_SubmissionFragment } from '@/modules/forms/graphql/FormSubmissions.generated';
import { readRecursiveFormConfig } from '@/modules/forms/recursiveFormConfig';
import { SubmissionStatus } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';
import { AnswerReviewSaveState, AnswerReviewValue, AnswerReviewViewModel, AnswerReviewWorkbenchState } from './AnswerReviewWorkbench.types';
import { FormSubmissionAdapterError } from './formSubmissionFields';
import { RecursiveAnswerNode } from './recursiveAnswer';
import { recursiveSubmissionSections } from './recursiveSubmissionAnswers';
type Submission = FormsAnswerReview_SubmissionFragment;
export function getAnswerLockVersions(submission: Submission): Map<string, number> {
  return new Map((submission.answers ?? []).map(answer => [answer.fieldID, answer.lockVersion]));
}
export function formSubmissionAnswerReviewAdapter({
  submission,
  saveState,
  draftValues = new Map(),
  recursiveDrafts = new Map(),
  readOnly = false
}: {
  submission: Submission;
  saveState: AnswerReviewSaveState;
  draftValues?: ReadonlyMap<string, AnswerReviewValue>;
  recursiveDrafts?: ReadonlyMap<string, RecursiveAnswerNode>;
  /** Disables editing regardless of submission status. */
  readOnly?: boolean;
}): AnswerReviewWorkbenchState {
  const sources = getNodes(submission.sourceDocuments);
  if (submission.status === SubmissionStatus.Draft) {
    return {
      kind: 'extracting',
      sourceDocumentName: sources[0]?.name,
      message: 'Preparing the source documents for extraction…'
    };
  }
  if (submission.status === SubmissionStatus.Extracting) {
    return {
      kind: 'extracting',
      sourceDocumentName: sources[0]?.name,
      message: 'This can take up to 5 minutes, you can leave this page'
    };
  }
  const config = submission.formVersion.schemaVersion === 1 ? readRecursiveFormConfig(submission.formVersion.config) : undefined;
  if (!config) throw new FormSubmissionAdapterError('Unsupported published form configuration.');
  const answers = new Map((submission.answers ?? []).map(answer => [answer.fieldID, answer]));
  const sections = recursiveSubmissionSections(config, answers, recursiveDrafts, draftValues);
  const hasAnswers = (submission.answers ?? []).some(answer => answer.currentRevision != null);
  if (submission.status === SubmissionStatus.Failed && !hasAnswers) {
    return {
      kind: 'error',
      message: 'The answers could not be extracted.'
    };
  }
  let mode: AnswerReviewViewModel['mode'] = 'readOnly';
  if (!readOnly && submission.status === SubmissionStatus.InReview) mode = 'editable';
  if (!readOnly && submission.status === SubmissionStatus.Completed) mode = 'completed';
  const review: AnswerReviewViewModel = {
    mode,
    saveState,
    sourceDocuments: sources,
    sections
  };
  if (submission.status === SubmissionStatus.Failed) {
    review.extractionError = 'Extraction stopped before all answers were generated.';
  }
  return {
    kind: 'ready',
    review
  };
}